"use client"

import { createContext, useState, useContext, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import api from "../services/api"

const AuthContext = createContext()

const selectedOrganizationKey = (userId) => `selectedOrganization.${userId}`

const readSavedOrganization = (userId) => {
  try {
    return JSON.parse(sessionStorage.getItem(selectedOrganizationKey(userId)))
  } catch (error) {
    console.warn("Could not read the saved organization:", error)
    return null
  }
}

const saveOrganization = (userId, organization) => {
  try {
    if (organization) {
      sessionStorage.setItem(selectedOrganizationKey(userId), JSON.stringify(organization))
    } else {
      sessionStorage.removeItem(selectedOrganizationKey(userId))
    }
  } catch (error) {
    console.warn("Could not save the selected organization:", error)
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [token, setToken] = useState(localStorage.getItem("token") || null)
  const [isAuthenticated, setIsAuthenticated] = useState(!!token)
  const [isLoading, setIsLoading] = useState(true)
  const [selectedOrganization, setSelectedOrganization] = useState(null)
  const navigate = useNavigate()

  // Set token in axios headers
  useEffect(() => {
    if (token) {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`
    } else {
      delete api.defaults.headers.common["Authorization"]
    }
  }, [token])

  // Check if user is authenticated on initial load
  useEffect(() => {
    const checkAuth = async () => {
      // Skip auth check on login, register, forgot password pages
      const isAuthPage =
        window.location.pathname.includes("/login") ||
        window.location.pathname.includes("/register") ||
        window.location.pathname.includes("/forgot-password") ||
        window.location.pathname.includes("/reset-password")

      if (token && !isAuthPage) {
        try {
          // Fetch user profile
          const response = await api.get("/users/profile")
          setUser(response.data.data)
          setSelectedOrganization(readSavedOrganization(response.data.data._id))
          setIsAuthenticated(true)
        } catch (error) {
          console.error("Auth check failed:", error)
          // Don't clear the token on every error - only on 401 Unauthorized
          if (error.response && error.response.status === 401) {
            localStorage.removeItem("token")
            setToken(null)
            setUser(null)
            setIsAuthenticated(false)
          } else {
            // For other errors, still consider the user authenticated
            // This prevents logout on network errors or server issues
            setIsAuthenticated(true)
          }
        }
      } else if (!token) {
        setIsAuthenticated(false)
        setUser(null)
      }
      setIsLoading(false)
    }

    checkAuth()
  }, [token])

  const login = async (email, password) => {
    const response = await api.post("/auth/login", { email, password })
    const { token, data } = response.data
    localStorage.setItem("token", token)
    setToken(token)
    setUser(data)
    setSelectedOrganization(readSavedOrganization(data._id))
    setIsAuthenticated(true)
    return data
  }

  const logout = async () => {
    try {
      // Call the logout endpoint
      await api.post("/auth/logout")
    } catch (error) {
      console.error("Logout error:", error)
    } finally {
      // Clear local storage and state regardless of API success
      saveOrganization(user?._id, null)
      localStorage.removeItem("token")
      setToken(null)
      setUser(null)
      setIsAuthenticated(false)
      setSelectedOrganization(null)
      navigate("/login")
    }
  }

  const selectOrganization = (organization) => {
    setSelectedOrganization(organization)
    saveOrganization(user?._id, organization)
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated,
        isLoading,
        login,
        logout,
        selectedOrganization,
        selectOrganization,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)

